import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Icons } from '@/src/modules/components/Icons';
import { Button } from '@/src/modules/components/Button';
import type { ApiInvitation } from '@/src/types';
import { useFormat } from '@/src/infrastructure/lib/format';
import { InvitationStatusBadge } from './InvitationStatusBadge';

interface Props {
  invitation: ApiInvitation;
  busy?: boolean;
  onArchive: (id: string) => void;
  onRestore: (id: string) => void;
  onCancel: (id: string) => void;
  /** Open the wizard to continue editing a DRAFT invitation. */
  onEdit?: (id: string) => void;
  /** Permanently delete a DRAFT invitation (shows a confirm dialog upstream). */
  onDelete?: (id: string) => void;
  /** Open full-screen preview of a DRAFT invitation without publishing. */
  onPreview?: (invitation: ApiInvitation) => void;
  /** Open the Share modal for the invitation. */
  onShare?: (invitation: ApiInvitation) => void;
  onOpenHistory: (id: string) => void;
  onCopyLink: (url: string) => void;
  /** Navigate to the Memory Capsule Gift dashboard (birthday-premium only). */
  onCapsuleGift?: (id: string) => void;
  /** Extend an EXPIRED invitation (restore access + extend expiration). */
  onExtend?: (id: string, extensionDays: number) => void;
}

/**
 * Card variant of an invitation, matching the spec's mock: cover image,
 * status badge, dates, analytics counters (views / RSVPs / shares), and
 * quick actions tuned to the current lifecycle state.
 *
 * ⚡ Bolt Optimization:
 * Wrapped in React.memo() to prevent unnecessary re-renders when the parent
 * list (InvitationsPage) updates state (like the active tab or debounced search)
 * and the individual invitation data hasn't changed.
 *
 * Impact: Prevents N re-renders per list update, reducing CPU time and improving
 * scroll / interaction performance for large grids of invitations.
 */
export const InvitationCard = memo(function InvitationCard({
  invitation,
  busy,
  onArchive,
  onRestore,
  onCancel,
  onEdit,
  onDelete,
  onPreview,
  onShare,
  onOpenHistory,
  onCopyLink,
  onCapsuleGift,
  onExtend,
}: Props) {
  const fmt = useFormat();
  const formatDate = (iso?: string | null) => (iso ? fmt.date(iso) : '—');

  // Calculate time remaining/expired
  const getExpiryStatus = () => {
    if (!invitation.expiresAt) return null;
    const expiresTime = new Date(invitation.expiresAt).getTime();
    const nowTime = Date.now();
    const diffMs = expiresTime - nowTime;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays > 0) {
      return { type: 'remaining', days: diffDays, text: `${diffDays} day${diffDays === 1 ? '' : 's'} left` };
    } else if (diffDays === 0) {
      return { type: 'expiring', days: 0, text: 'Expires today' };
    } else {
      return { type: 'expired', days: diffDays, text: `Expired ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'} ago` };
    }
  };

  const expiryStatus = getExpiryStatus();

  const cover = invitation.mediaUrls?.[0];
  const isDraft = invitation.status === 'DRAFT';
  const isArchived = invitation.status === 'ARCHIVED';
  const isPublished = invitation.status === 'PUBLISHED';
  const isExpired = invitation.status === 'EXPIRED';
  const isBirthdayPremium = invitation.templateId === 'birthday-premium';
  const isInactive =
    invitation.status === 'EXPIRED' ||
    invitation.status === 'CANCELLED' ||
    invitation.status === 'SUSPENDED' ||
    isArchived;

  return (
    <motion.div
      layout
      whileHover={{ y: -4 }}
      className="bg-surface rounded-[2rem] border border-outline-variant/30 shadow-sm overflow-hidden flex flex-col"
    >
      <div className="h-44 relative bg-surface-low overflow-hidden">
        {cover ? (
          <img
            src={cover}
            alt={invitation.eventName}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-surface-low to-outline-variant/10 flex items-center justify-center">
            <Icons.Calendar size={42} className="text-on-surface-variant/20" />
          </div>
        )}
        <div className="absolute top-3 right-3">
          <InvitationStatusBadge status={invitation.status} />
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-lg font-bold text-on-surface mb-1 line-clamp-1">
          {invitation.eventName}
        </h3>
        <p className="text-xs text-on-surface-variant mb-3 line-clamp-1">
          Host: {invitation.hostName || '—'}
        </p>

        <dl className="grid grid-cols-3 gap-2 text-[11px] text-on-surface-variant font-medium mb-4">
          <div>
            <dt className="uppercase tracking-wide">Event</dt>
            <dd className="text-on-surface font-semibold">{formatDate(invitation.eventDate)}</dd>
          </div>
          <div>
            <dt className="uppercase tracking-wide">Expires</dt>
            <dd className="text-on-surface font-semibold">
              {formatDate(invitation.expiresAt ?? undefined)}
            </dd>
            {expiryStatus && (
              <dd className={`text-xs mt-1 font-bold ${
                expiryStatus.type === 'expired' ? 'text-status-failed' :
                expiryStatus.type === 'expiring' ? 'text-status-warning' :
                'text-status-success'
              }`}>
                {expiryStatus.text}
              </dd>
            )}
          </div>
          <div>
            <dt className="uppercase tracking-wide">Created</dt>
            <dd className="text-on-surface font-semibold">{formatDate(invitation.createdAt)}</dd>
          </div>
        </dl>

        <div className="grid grid-cols-3 gap-2 text-xs text-on-surface-variant font-medium border-t border-outline-variant/30 pt-3 mb-4">
          <span>
            <strong className="text-on-surface tabular-nums">{invitation.totalViews ?? 0}</strong> views
          </span>
          <span>
            <strong className="text-on-surface tabular-nums">{invitation.totalRsvps ?? 0}</strong> RSVPs
          </span>
          <span>
            <strong className="text-on-surface tabular-nums">{invitation.totalShares ?? 0}</strong> shares
          </span>
        </div>

        <div className="mt-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            {isPublished && invitation.hostedUrl && (
              <>
                <a
                  href={invitation.hostedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-primary/10 text-primary"
                  title="Open invitation"
                  aria-label="Open invitation"
                >
                  <Icons.ExternalLink size={16} />
                </a>
                <button
                  type="button"
                  onClick={() => onCopyLink(invitation.hostedUrl!)}
                  className="p-2 rounded-full hover:bg-primary/10 text-primary"
                  title="Copy link"
                  aria-label="Copy link"
                >
                  <Icons.Copy size={16} />
                </button>
                {onShare && (
                  <button
                    type="button"
                    onClick={() => onShare(invitation)}
                    className="p-2 rounded-full hover:bg-primary/10 text-primary"
                    title="Share invitation"
                    aria-label="Share invitation"
                  >
                    <Icons.Share size={16} />
                  </button>
                )}
              </>
            )}
            <button
              type="button"
              onClick={() => onOpenHistory(invitation.id)}
              className="p-2 rounded-full hover:bg-primary/10 text-primary"
              title="View timeline"
              aria-label="View timeline"
            >
              <Icons.History size={16} />
            </button>
            {!isDraft && (
              <Link
                to={`/dashboard/invitations/${invitation.id}/guests`}
                className="p-2 rounded-full hover:bg-primary/10 text-primary"
                title="Manage guests"
                aria-label="Manage guests"
              >
                <Icons.Users size={16} />
              </Link>
            )}
            {!isDraft && (
              <Link
                to={`/dashboard/invitations/${invitation.id}/rsvps`}
                className="p-2 rounded-full hover:bg-primary/10 text-primary"
                title={`View RSVPs${invitation.totalRsvps ? ` (${invitation.totalRsvps})` : ''}`}
              >
                <Icons.Rsvp size={16} />
              </Link>
            )}
            {!isDraft && (invitation.occasionType === 'WEDDING' || invitation.occasionType === 'BIRTHDAY') && (
              <Link
                to={`/dashboard/invitations/${invitation.id}/blessings`}
                className="p-2 rounded-full hover:bg-primary/10 text-primary"
                title={invitation.occasionType === 'WEDDING' ? "Manage blessings" : "Manage wishes"}
                aria-label={invitation.occasionType === 'WEDDING' ? "Manage blessings" : "Manage wishes"}
              >
                <Icons.Feedback size={16} />
              </Link>
            )}
            {isBirthdayPremium && onCapsuleGift && (
              <button
                type="button"
                onClick={() => onCapsuleGift(invitation.id)}
                className="p-2 rounded-full hover:bg-primary/10 text-primary"
                title="Memory Capsule Gift"
                aria-label="Memory Capsule Gift"
              >
                <Icons.Gift size={16} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-1">
            {isDraft ? (
              <>
                {onDelete && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => onDelete(invitation.id)}
                    className="text-status-failed font-bold"
                    title="Delete draft"
                    aria-label="Delete draft"
                  >
                    <Icons.Trash size={14} />
                  </Button>
                )}
                {onPreview && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => onPreview(invitation)}
                    className="flex items-center gap-1"
                    title="Preview draft"
                    aria-label="Preview draft"
                  >
                    <Icons.Eye size={13} /> Preview
                  </Button>
                )}
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() => onEdit?.(invitation.id)}
                >
                  Continue editing
                </Button>
              </>
            ) : isArchived ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => onRestore(invitation.id)}
                className="text-primary font-bold"
              >
                <Icons.ArchiveRestore size={14} />
                <span className="ml-1">Restore</span>
              </Button>
            ) : isExpired ? (
              onExtend && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => onExtend(invitation.id, 7)}
                  className="text-primary font-bold flex items-center gap-1"
                  title="Restore access: costs 2 credits"
                >
                  <Icons.RotateCcw size={14} />
                  <span>Extend (2 credits)</span>
                </Button>
              )
            ) : (
              <>
                {/* Edit action — available on PUBLISHED cards so the host can update live content and republish */}
                {isPublished && onEdit && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => onEdit(invitation.id)}
                    className="flex items-center gap-1"
                    title="Edit and republish"
                    aria-label="Edit and republish"
                  >
                    <Icons.Edit3 size={13} /> Edit
                  </Button>
                )}
                {!isInactive && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => onCancel(invitation.id)}
                    className="text-on-surface-variant font-bold"
                    title="Cancel invitation"
                    aria-label="Cancel invitation"
                  >
                    <Icons.Ban size={14} />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => onArchive(invitation.id)}
                  className="text-on-surface-variant font-bold"
                  title="Archive"
                  aria-label="Archive"
                >
                  <Icons.Archive size={14} />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
});
